import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook for astronomical data.
 *
 * @param customLocation optional { lat, lon, timezone } - overrides GPS
 * @param useGPS         if true and no customLocation, request browser geolocation
 */
export const useAstronomicalData = (customLocation = null, useGPS = true) => {
  const [gpsLocation, setGpsLocation] = useState(null);
  // gpsResolved is true ONLY when navigator.geolocation actually returned a
  // user-position. The silent Mecca fallback below sets gpsLocation for
  // calculation purposes but keeps gpsResolved=false so the UI doesn't
  // pretend it has the user's real location.
  const [gpsResolved, setGpsResolved] = useState(false);
  const [astronomicalData, setAstronomicalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user location via GPS (only when needed)
  useEffect(() => {
    if (customLocation || !useGPS) return;

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
          setGpsResolved(true);
        },
        () => {
          // Default to Mecca coordinates so calculations still produce output,
          // but mark gpsResolved=false so the UI does NOT advertise this as
          // the user's real GPS location.
          setGpsLocation({ lat: 21.4225, lon: 39.8262 });
          setGpsResolved(false);
        }
      );
    } else {
      setGpsLocation({ lat: 21.4225, lon: 39.8262 });
      setGpsResolved(false);
    }
  }, [customLocation, useGPS]);

  // Effective location
  const effectiveLocation = customLocation || gpsLocation;

  // Fetch astronomical data when location changes
  useEffect(() => {
    if (!effectiveLocation) return;

    const fetchData = async () => {
      try {
        const timezone = customLocation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await axios.get(`${API}/location-time`, {
          params: {
            lat: effectiveLocation.lat,
            lon: effectiveLocation.lon,
            tz: timezone
          }
        });
        setAstronomicalData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching astronomical data:', err);
        setError('Failed to fetch astronomical data');
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [effectiveLocation?.lat, effectiveLocation?.lon, customLocation?.timezone]);

  return {
    location: effectiveLocation,
    gpsResolved,
    astronomicalData,
    loading,
    error
  };
};

export const useCurrentTime = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return currentTime;
};
